import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { pick } from "lodash";
import { InfiniteScrollList } from "@/components/common/scroll";
import { useGetOrderListQuery } from "@/features/order";
import { Button } from "@/components/ui/button";
import { Calendar, Plus, SlidersHorizontal } from "lucide-react";
import { convertToUTCDateTime, dateFormateDDMMYYYY, getStatusColors } from "@/utils/common";
import { currencyFormatter } from "@/utils";
import { OrderStateEnum } from "@/enum";
import { Chip } from "@/components/views";
import { LIMIT } from "@/constants";
import { Order } from "@/interfaces";
import { useMemo, useState } from "react";
import "./Sales.css";
import { getTodayMidnightLocal } from "@/features/dashboard/utils";
import { DateRangeCalendar } from "@/features/order/component/DateRangeCalendar";

import { appColors } from "@/constants/color";
export const Route = createFileRoute("/(app)/(store)/sales/")({
  component: RouteComponent,
});


function RouteComponent() {
  const navigate = useNavigate();
  const limit = LIMIT * 3;
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState("Today");
  const [showFilter, setShowFilter] = useState(true);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedRange, setSelectedRange] = useState<{ from: Date | null; to: Date | null }>({
    from: null,
    to: null,
  });
  const searchParams = Route.useSearch();
  const customerFilterParams = searchParams.customerFilter || {};
  const customerName = searchParams.customerName || '';

  const queryParams = useMemo(() => {
    const baseParams: any = {
      limit,
      page,
      ["seller-store-detail"]: true,
      ["order-detail"]: true,
      ["buyer-detail"]: true,
      ...customerFilterParams,
    };

    if (filter === "All Orders") {
      return baseParams;
    }

    if (filter === "Today") {
      const todayMidnightLocal = getTodayMidnightLocal();
      const todayMidnightUTC = convertToUTCDateTime(todayMidnightLocal);
      const endOfDay = new Date(todayMidnightLocal.getTime() + 24 * 60 * 60 * 1000 - 1);
      const endOfDayUTC = convertToUTCDateTime(endOfDay);

      return {
        ...baseParams,
        ["from-date"]: todayMidnightUTC,
        ["to-date"]: endOfDayUTC,
      };
    }

    if (filter === "Custom Range" && selectedRange.from && selectedRange.to) {
      const fromUTC = convertToUTCDateTime(selectedRange.from);
      const toUTC = convertToUTCDateTime(
        new Date(selectedRange.to.getTime() + 24 * 60 * 60 * 1000 - 1)
      );

      return {
        ...baseParams,
        ["from-date"]: fromUTC,
        ["to-date"]: toUTC,
      };
    }

    const statusParams = {
      ...baseParams,
      status: filter,
    };
    
    return statusParams;
  }, [limit, page, filter, selectedRange, customerFilterParams]);

  const { data: orders = [] } = useGetOrderListQuery(queryParams);

  const handleDateSelect = (from: Date, to: Date) => {
    setSelectedRange({ from, to });
    setFilter("Custom Range");
    setPage(1);
    setShowDatePicker(false);
  };

  const handleCalendarClose = () => {
    setShowDatePicker(false);
  };

  const handleCalendarCancel = () => {
    setSelectedRange({ from: null, to: null });
    setShowDatePicker(false);
    setFilter("All Orders")
  };

  const shouldEnableInfiniteScroll = useMemo(() => {
    // Disable infinite scrolling for date range filters (Today and Custom Range)
    return filter !== "Today" && filter !== "Custom Range";
  }, [filter]);

  const renderOrderItem = (item: Order) => {
    const { statusColor, bgColor } = getStatusColors(
      item.status !== OrderStateEnum.Cancelled && item.status !== OrderStateEnum.Returned
        ? item?.payment?.paymentStatus
        : item.status
    );

    const visibleProducts = item.itemList?.slice(0, 2) || [];
    const remainingProductsCount = Math.max(
      (item.itemList?.length || 0) - 2,
      0
    );

    return (
      <div
        className="orderItem"
        key={item._id}
        onClick={() =>
          navigate({
            to: "/sales/$orderId",
            params: { orderId: item._id },
            search: {
              customerFilter: customerFilterParams,
              customerName: customerName
            }
          })
        }
      >
        <div className="orderCardGradient">
          <div className="orderCardHeader u-flexRow u-justifyBetween">
            <div className="orderMeta u-flexCol">
              <p className="billTotalText">
                Bill Total:{" "}
                <span className="billAmountValue">
                  {currencyFormatter(item.totalAmount)}
                </span>
              </p>
              <text className="captionText">{item?.sellerStore?.name}</text>
              <text className="invoiceTag">#{item?.invoiceNumber}</text>
            </div>

            <div
              className="statusBadge u-pill"
              style={{ backgroundColor: bgColor }}
            >
              <span style={{ color: statusColor }}>
                {item.status !== OrderStateEnum.Cancelled && item.status !== OrderStateEnum.Returned
                  ? item.payment?.paymentStatus
                  : item.status}
              </span>
            </div>
          </div>
        </div>

        <div className="orderContent">
          {visibleProducts.length > 0 ? (
            <>
              <div className="productsList">
                {visibleProducts.map((productItem, index) => (
                  <div key={index} className="productRow u-alignCenter">
                    {productItem.product?.image && (
                      <img
                        className="productThumb u-imgSmall"
                        src={productItem.product.image}
                        alt={productItem.product?.name || "product image"}
                      />
                    )}
                    <span className="qtyText body">
                      {productItem.unitWiseQuantity?.quantity}
                    </span>
                    <span className="productNameText body u-ellipsis">
                      {" X " + productItem.product?.name ||
                        "Product not specified"}
                    </span>
                  </div>
                ))}
              </div>

              {remainingProductsCount > 0 && (
                <div className="moreItems">
                  <text className="captionText">
                    +{remainingProductsCount} more item
                    {remainingProductsCount !== 1 ? "s" : ""}
                  </text>
                </div>
              )}
            </>
          ) : (
            <div className="emptyProducts">
              <text className="emptyProductsText">
                No products in this order
              </text>
            </div>
          )}

          <div className="statusRow u-justifyBetween">
            <text className="captionText">
              {item.payment?.paymentStatus === "Paid" ? "Paid on" : "Due on"}{" "}
              {dateFormateDDMMYYYY(item.createdAt)}
            </text>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="ordersContainer">
      <div className="filterHeader">
        <div className="filterBar u-flexRow u-justifyBetween u-alignCenter">
          <div className="filterScroll u-flexRow u-gap12 u-scrollHidden">
            <div className="filterControl u-flexRow u-alignCenter">
              <SlidersHorizontal
                onClick={() => setShowFilter(!showFilter)}
                className="filterIcon"
              />
              <span>Filter</span>
            </div>
            {showFilter && (
              <>
                <Button
                  className={filter === "Custom Range" ? "chipActive" : ""}
                  onClick={() => {
                    setShowDatePicker(true);
                  }}
                  variant="outline"
                  
                >
                  
                  <div style={{display: "flex" , flexDirection: "row" , alignItems: "center" , gap: "4px"}}>
                    <Calendar/>
                    {selectedRange.from && selectedRange.to
                    ? `${dateFormateDDMMYYYY(selectedRange.from)} - ${dateFormateDDMMYYYY(selectedRange.to)}`
                    : "Custom Range"}
                  </div>
                  
                </Button>

                <Chip
                  className={filter === "All Orders" ? "chipActive" : "chip"}
                  onClick={() => {
                    setFilter("All Orders");
                    setPage(1);
                    setSelectedRange({ from: null, to: null });
                  }}
                >
                  All Orders
                </Chip>
                <Chip
                  className={filter === "Today" ? "chipActive" : "chip"}
                  onClick={() => {
                    setFilter("Today");
                    setPage(1);
                    setSelectedRange({ from: null, to: null });
                  }}
                >
                  Today
                </Chip>
                <Chip
                  className={
                    filter === OrderStateEnum.Returned ? "chipActive" : "chip"
                  }
                  onClick={() => {
                    setFilter(OrderStateEnum.Returned);
                    setPage(1);
                    setSelectedRange({ from: null, to: null });
                  }}
                >
                  {OrderStateEnum.Returned}
                </Chip>

                {Object.values(
                  pick(OrderStateEnum, ["Cancelled", "Delivered"])
                ).map((item) => (
                  <Chip
                    className={filter === item ? "chipActive" : "chip"}
                    key={item}
                    onClick={() => {
                      setFilter(item);
                      setPage(1);
                      setSelectedRange({ from: null, to: null });
                    }}
                  >
                    {item}
                  </Chip>
                ))}
              </>
            )}
          </div>

          <div className="createOrderWrap">
            <Button onClick={() => navigate({ to: "/sales/create" })}>
              <Plus className="w-4 h-4 mr-2" />
              Create New Order
            </Button>
          </div>
        </div>
        
        {customerName && (
          <div className="customerFilterInfo" style={{
            padding: '8px 16px',
            backgroundColor: appColors.salesInfoBg,
            border: `1px solid ${appColors.salesInfoBorder}`,
            borderRadius: '8px',
            margin: '8px 0'
          }}>
            <span style={{ fontWeight: '500', color: appColors.salesInfoText }}>
              Showing orders for: {customerName}
            </span>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => {
                navigate({
                  to: "/sales/",
                  search: {}
                });
              }}
              style={{ marginLeft: '12px' }}
            >
              Clear Filter
            </Button>
          </div>
        )}
      </div>
      <div className="ordersList">
        <InfiniteScrollList
          items={orders}
          renderItem={renderOrderItem}
          loadMore={shouldEnableInfiniteScroll ? () => setPage((pre) => pre + 1) : undefined}
          hasMore={shouldEnableInfiniteScroll ? limit * page <= orders?.length : false}
          maxHeight={"100dvh"}
        />
      </div>
      <DateRangeCalendar
        isOpen={showDatePicker}
        onClose={handleCalendarClose} 
        onCancel={handleCalendarCancel}
        onDateSelect={handleDateSelect}
      />
    </div>
  );
}